import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import { Description, Assessment } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getJobFiles } from '@/api/jobs';
import { ModelType } from '@/types/colabfold';

interface ESMIFResultsType {
  params?: {
    num_designs?: number;
    temperature?: number;
    input_pdb?: string;
    query_sequence?: string;
  };
  files?: string[];
}

interface ESMIFResultsProps {
  results: ESMIFResultsType;
  jobId: string;
}

interface Sequence {
  id: string;
  sequence: string;
  recovery?: number;
}

export const ESMIFResults: React.FC<ESMIFResultsProps> = ({ results, jobId }) => {
  // Use the files API to get the actual files
  const { data: filesData } = useQuery({
    queryKey: ['jobFiles', jobId, ModelType.ESMIF],
    queryFn: () => getJobFiles(ModelType.ESMIF, jobId),
  });
  
  // Extract parameters from results
  const params = results?.params || {};
  const numDesigns = params.num_designs || 'N/A';
  const temperature = params.temperature || 'N/A';
  
  // State for storing parsed sequences and metrics
  const [sequences, setSequences] = useState<{
    native: string;
    designs: Sequence[];
    recoveryStats: string;
    isLoading: boolean;
  }>({
    native: '',
    designs: [],
    recoveryStats: '',
    isLoading: true
  });
  
  // Process files data to extract sequences and metrics
  useEffect(() => {
    const fetchSequences = async () => {
      if (!filesData?.files) return;
      
      const outputFiles = filesData.files.filter((file: string) => 
        !file.startsWith('logs/') && file !== 'status.txt'
      );
      
      // Find FASTA files and recovery metrics file
      const nativeSeqFile = outputFiles.find((file: string) => file.includes('native') && file.endsWith('.fasta'));
      const designsSeqFile = outputFiles.find((file: string) => file.includes('sampled') && file.endsWith('.fasta'));
      const recoveryFile = outputFiles.find((file: string) => file.includes('recovery'));
      
      try {
        // Get direct download URL for files
        const getFileUrl = (filename: string) => {
          return `/api/esmif/jobs/${jobId}/download/?file=${filename}`;
        };

        // Parse native sequence
        let nativeSeq = '';
        if (nativeSeqFile) {
          const nativeResponse = await fetch(getFileUrl(nativeSeqFile));
          const nativeContent = await nativeResponse.text();
          // Parse FASTA format - content after the first line (header)
          const nativeLines = nativeContent.split('\n');
          if (nativeLines.length > 1) {
            nativeSeq = nativeLines.slice(1).join('').trim();
          }
        }
        
        // Parse designed sequences
        const designs: Sequence[] = [];
        if (designsSeqFile) {
          const designsResponse = await fetch(getFileUrl(designsSeqFile));
          const designsContent = await designsResponse.text();
          
          // Parse FASTA format - extract sequences
          const lines = designsContent.split('\n');
          let currentId = '';
          let currentSeq = '';
          
          for (const line of lines) {
            if (line.startsWith('>')) {
              // Save previous sequence if exists
              if (currentId && currentSeq) {
                designs.push({
                  id: currentId,
                  sequence: currentSeq
                });
              }
              // Start new sequence
              currentId = line.substring(1).trim();
              currentSeq = '';
            } else if (line.trim()) {
              // Add to current sequence
              currentSeq += line.trim();
            }
          }
          
          // Add the last sequence
          if (currentId && currentSeq) {
            designs.push({
              id: currentId,
              sequence: currentSeq
            });
          }
        }
        
        // Parse recovery stats if available
        let recoveryStats = '';
        if (recoveryFile) {
          const recoveryResponse = await fetch(getFileUrl(recoveryFile));
          recoveryStats = await recoveryResponse.text();
          
          // Update designs with recovery values
          const recoveryLines = recoveryStats.split('\n');
          for (const line of recoveryLines) {
            if (line.includes('Design') && line.includes(':')) {
              const parts = line.split(':');
              const designId = parts[0].trim().replace('Design ', '');
              const recoveryValue = parseFloat(parts[1].trim());
              
              // Find and update the matching design
              const designIndex = designs.findIndex(d => d.id === `design_${designId}`);
              if (designIndex >= 0) {
                designs[designIndex].recovery = recoveryValue;
              }
            }
          }
        }
        
        setSequences({
          native: nativeSeq,
          designs,
          recoveryStats,
          isLoading: false
        });
      } catch (error) {
        console.error("Error fetching sequence data:", error);
        setSequences(prev => ({...prev, isLoading: false}));
      }
    };
    
    if (filesData?.files) {
      fetchSequences();
    }
  }, [filesData, jobId]);
  
  // Function to get file type icon
  const getFileIcon = (filename: string) => {
    if (filename.endsWith('.fasta')) {
      return <Description color="primary" />;
    } else if (filename.includes('recovery')) {
      return <Assessment color="secondary" />;
    } else {
      return <Description />;
    }
  };
  
  // Function to get download URL for a file
  const getFileUrl = (filename: string) => {
    return `/api/esmif/jobs/${jobId}/download/?file=${filename}`;
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        ESM-IF Results
      </Typography>
      
      {/* Design Parameters Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Design Parameters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Number of Designs</Typography>
            <Typography variant="body1" gutterBottom>{numDesigns}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Temperature</Typography>
            <Typography variant="body1" gutterBottom>{temperature}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              ESM-IF performs inverse folding to predict protein sequences that would fold into the given 3D structure.
              View the <a href={`#/jobs/${jobId}/esmif?tab=1&file=input.pdb`}>3D Structure tab</a> to examine the input structure.
            </Alert>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Design Results */}
      {sequences.isLoading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Loading sequence data...
          </Typography>
        </Paper>
      ) : sequences.designs.length > 0 ? (
        <Grid container spacing={3}>
          {/* Sequence Recovery Summary */}
          {sequences.recoveryStats && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Divider sx={{ my: 2 }} />
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Design</TableCell>
                          <TableCell align="right">Sequence Recovery</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sequences.designs.map((design) => (
                          <TableRow key={design.id}>
                            <TableCell component="th" scope="row">
                              {design.id}
                            </TableCell>
                            <TableCell align="right">
                              {design.recovery !== undefined ? `${(design.recovery * 100).toFixed(1)}%` : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Sequences */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Designed Sequences
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {/* Native sequence */}
                {sequences.native && (
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Native Sequence
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', overflowX: 'auto' }}>
                      <Typography variant="body2" fontFamily="monospace" whiteSpace="pre-wrap">
                        {sequences.native}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {/* Design sequences */}
                {sequences.designs.map((design) => (
                  <Box key={design.id} mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      {design.id} 
                      {design.recovery !== undefined && 
                        <Chip 
                          label={`Recovery: ${(design.recovery * 100).toFixed(1)}%`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      }
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', overflowX: 'auto' }}>
                      <Typography variant="body2" fontFamily="monospace" whiteSpace="pre-wrap">
                        {design.sequence}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Files */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Output Files
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {filesData?.files ? (
                  <List>
                    {filesData.files
                      .filter((file: string) => !file.startsWith('logs/') && file !== 'status.txt')
                      .map((file: string, index: number) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            {getFileIcon(file)}
                          </ListItemIcon>
                          <ListItemText 
                            primary={file} 
                            secondary={
                              file.endsWith('.fasta') ? 'FASTA sequence file' : 
                              file.includes('recovery') ? 'Sequence recovery metrics' : 
                              file.endsWith('.pdb') ? 'Structure file' : ''
                            }
                          />
                          <Button 
                            variant="outlined" 
                            size="small"
                            href={getFileUrl(file)}
                            target="_blank"
                            download
                          >
                            Download
                          </Button>
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <Typography variant="body1" align="center">
                    No output files available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No design results available.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ESMIFResults; 